import { getScanStatus, getScanLocationLabel, type ScanStatusInput } from '@repo/ui';

/**
 * Shared scan filtering for the certificate Scans tab (VERA-476).
 *
 * The list used to own this logic privately, and the map was handed nothing but a
 * certificate id — so search, status, owner, date and location controls silently
 * applied to the list ONLY. Switching to map view appeared to reset every filter.
 * Both surfaces now run the same predicate over their own fetched rows.
 */

export interface ScanFilterCriteria {
  query?: string;
  selectedStatuses?: string[];
  selectedLocationIds?: string[];
  dateFrom?: string;
  dateTo?: string;
  selectedRange?: 'last_month' | 'last_3m' | 'last_6m' | 'last_12m' | null;
}

/** Loose row shape — callers pass action-log rows with varying extras. */
type ScanLike = {
  user?: {
    name?: string;
    first_name?: string;
    last_name?: string;
    email?: string;
  } | null;
  created_at: string;
  metadata?: any;
  type?: string | null;
  status?: string | null;
};

const matchesQuery = (scan: ScanLike, normalizedQuery: string): boolean => {
  if (!normalizedQuery) return true;

  const first = (scan.user?.first_name || '').toLowerCase();
  const last = (scan.user?.last_name || '').toLowerCase();
  const haystack = [
    scan.user?.name || '',
    first,
    last,
    `${first} ${last}`.trim(),
    scan.user?.email || '',
    getScanLocationLabel(scan.metadata) || '',
    scan.metadata?.city || '',
    scan.metadata?.country || '',
    scan.type || '',
    // The status LABEL is searchable too, so typing "opened" or "suspicious" finds those
    // rows — the label is what the user actually sees on the row.
    //
    // ⚠️ It is the LABEL, not the underlying key, so "tampered" matches nothing: the
    // resolver renames that outcome to "Opened" (VERA-628 / VRG-491). The glossary entry
    // `scan-status-opened` does carry `tampered` as an alias, so wiring aliases in here
    // would make the old word work again — deliberately not done, because search should
    // find what is on screen and a hidden second vocabulary is its own kind of surprise.
    getScanStatus(scan as ScanStatusInput).label,
  ]
    .join(' ')
    .toLowerCase();

  return haystack.includes(normalizedQuery);
};

const matchesDate = (scan: ScanLike, criteria: ScanFilterCriteria): boolean => {
  const scanDate = new Date(scan.created_at);
  const now = new Date();
  const { selectedRange, dateFrom, dateTo } = criteria;

  if (selectedRange) {
    const monthsBack = {
      last_month: 1,
      last_3m: 3,
      last_6m: 6,
      last_12m: 12,
    }[selectedRange];

    if (monthsBack === undefined) return true;

    const fromDate = new Date(now.getFullYear(), now.getMonth() - monthsBack, now.getDate());
    return scanDate >= fromDate && scanDate <= now;
  }

  if (dateFrom || dateTo) {
    if (dateFrom && scanDate < new Date(dateFrom)) return false;
    if (dateTo && scanDate > new Date(dateTo)) return false;
  }

  return true;
};

/** Apply every active filter. Returns a new array; never mutates the input. */
export const filterScans = <T extends ScanLike>(
  scans: T[],
  criteria: ScanFilterCriteria = {},
): T[] => {
  const normalizedQuery = (criteria.query || '').trim().toLowerCase();
  const statuses = criteria.selectedStatuses || [];
  const locationIds = criteria.selectedLocationIds || [];

  return scans.filter(scan => {
    // Inactive scans are excluded from the list regardless of filters.
    if ((scan.status || '').toLowerCase() === 'inactive') return false;

    if (!matchesQuery(scan, normalizedQuery)) return false;

    if (statuses.length && !statuses.includes(getScanStatus(scan as ScanStatusInput).key)) {
      return false;
    }

    if (locationIds.length && !locationIds.includes(getScanLocationLabel(scan.metadata) || '')) {
      return false;
    }

    return matchesDate(scan, criteria);
  });
};
